/**
 * Homepage product cards — READ MORE / READ LESS toggle + card equalization.
 * JS ONLY toggles the `.is-open` state class (+ aria) and measures geometry into
 * CSS custom props. All visuals/animation (overlay rise, description reveal,
 * caret flip, label swap) live in scss/sections/homepage-styles.scss, keyed off
 * `.is-open` + the vars set here.
 * Carousel = shared <carousel-swiper>; add-to-cart = Dawn <product-form>.
 *
 * Var written per card:
 *   --hp-content-bottom = 84 + maxH - thisCardHeight -> pins the bottom-anchored
 *     content so eyebrow tops align while read-more lands per-card (and, being
 *     bottom-anchored, never shifts on open — desc grows upward off the top).
 * The scrim rise origin (--hp-scrim-start) is derived from this in CSS via calc,
 * so there is no second measuring pass.
 */

const BASE_BOTTOM = 84; // tallest card's read-more offset from card bottom (matches foot height)

/**
 * Measure one section: pin each card's content bottom so eyebrow tops align to
 * the tallest card. Reads/writes are batched so the browser reflows once.
 */
function equalizeCards(section) {
  const cards = [...section.querySelectorAll('.homepage-product-cards__card')];
  if (!cards.length) return;

  // writes: reset collapsed cards so the measure below is natural. Open cards keep
  // their pinned --hp-content-bottom — resetting it would snap them to the 84px
  // default mid-expand (window resize / late fonts.ready while a card is open).
  cards.forEach((card) => {
    if (!card.classList.contains('is-open')) card.style.removeProperty('--hp-content-bottom');
  });

  // reads: cache each collapsed card's natural content height on the element, so an
  // open card still contributes its cached height to maxH (no drift while open).
  const heights = cards.map((card) => {
    if (!card.classList.contains('is-open')) {
      const content = card.querySelector('.homepage-product-cards__content');
      if (content) card.dataset.hpContentH = String(content.offsetHeight);
    }
    return Number(card.dataset.hpContentH) || 0;
  });
  const maxH = Math.max(0, ...heights);

  // media bottom-margin (the content/foot "well"): keep the image->eyebrow gap a
  // constant 12px no matter how tall the content wraps. Wider (desktop) cards wrap
  // less -> smaller maxH -> a fixed well would leave a big top gap; scaling it fixes that.
  // well = top gap(12) + tallest content(maxH) + read-more/foot reserve(BASE_BOTTOM).
  if (maxH) section.style.setProperty('--hp-well', `${maxH + BASE_BOTTOM + 12}px`);

  // pin: bottom offset = 84 + (maxH - thisHeight) -> shorter cards sit higher.
  cards.forEach((card, i) => {
    if (heights[i]) card.style.setProperty('--hp-content-bottom', `${BASE_BOTTOM + maxH - heights[i]}px`);
  });
}

export default function initProductCards() {
  const sections = document.querySelectorAll('.homepage-product-cards');
  if (!sections.length) return;

  sections.forEach((section) => {
    section.querySelectorAll('.homepage-product-cards__card').forEach((card) => {
      const btn = card.querySelector('[data-read-more-toggle]');
      if (!btn) return;

      const toggle = () => {
        const isOpen = card.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', String(isOpen));
      };

      btn.addEventListener('click', toggle);
      // clicking the description body toggles read-more too (image/title stay PDP links)
      const desc = card.querySelector('[data-read-more-text]');
      if (desc) desc.addEventListener('click', toggle);
    });
  });

  const equalizeAll = () => sections.forEach(equalizeCards);
  equalizeAll();
  // font swap changes text height -> re-measure once webfonts are ready
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(equalizeAll);

  // debounce with a timeout (not rAF): on a breakpoint change Swiper re-lays-out
  // the slides slightly later, so measure after it settles or content width is stale.
  let t = 0;
  window.addEventListener('resize', () => {
    clearTimeout(t);
    t = setTimeout(equalizeAll, 200);
  });
}
